import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://127.0.0.1:8787';

export interface ApiProduct {
  id: string;
  sku: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  originalPrice: number | null;
  categoryId: string | null;
  gender: 'hombre' | 'mujer' | 'unisex';
  brand: string | null;
  material: string | null;
  color: string | null;
  badge: string | null;
  isActive: boolean;
  isFeatured: boolean;
  category: { id: string; name: string; slug: string } | null;
  images: { id: string; url: string; altText: string | null; sortOrder: number; isPrimary: boolean }[];
  sizes: { id: string; size: string; stock: number }[];
}

export const productsApi = createApi({
  reducerPath: 'productsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('auth_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ['Product'],
  endpoints: (build) => ({
    getProducts: build.query<{ items: ApiProduct[]; total: number }, void>({
      query: () => '/api/v1/products?limit=100',
      transformResponse: (r: { data: { items: ApiProduct[]; total: number } }) => r.data,
      providesTags: ['Product'],
      keepUnusedDataFor: 60,
    }),
    createProduct: build.mutation<ApiProduct, unknown>({
      query: (body) => ({ url: '/api/v1/products', method: 'POST', body }),
      invalidatesTags: ['Product'],
    }),
    updateProduct: build.mutation<ApiProduct, { id: string; body: unknown }>({
      query: ({ id, body }) => ({ url: `/api/v1/products/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Product'],
    }),
    updateProductSizes: build.mutation<void, { id: string; sizes: { size: string; stock: number }[] }>({
      query: ({ id, sizes }) => ({ url: `/api/v1/products/${id}/sizes`, method: 'PUT', body: { sizes } }),
      invalidatesTags: ['Product'],
    }),
    deleteProduct: build.mutation<void, string>({
      query: (id) => ({ url: `/api/v1/products/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Product'],
    }),
    uploadImage: build.mutation<{ url: string }, { productId: string; form: FormData }>({
      query: ({ productId, form }) => ({
        url: `/api/v1/products/${productId}/images`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['Product'],
    }),
    deleteImage: build.mutation<void, string>({
      query: (imageId) => ({ url: `/api/v1/images/${imageId}`, method: 'DELETE' }),
      invalidatesTags: ['Product'],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useUpdateProductSizesMutation,
  useDeleteProductMutation,
  useUploadImageMutation,
  useDeleteImageMutation,
} = productsApi;
